import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { GoogleMap, StreetViewPanorama, StreetViewService, useLoadScript } from "@react-google-maps/api"

import { ZoomButtons, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useIsMobileDevice } from "@l2r-front/l2r-ui"

import { useImageCollectionDispatchContext, useImageCollectionStateContext } from "../../contexts/ImageCollectionContext"

import { StreetViewError } from "./StreetView.error"
import * as Styled from "./StreetView.styled"


export const StreetView = (props) => {

    const { active = true, initialPosition } = props
    const [streetViewState, setStreetViewState] = useState(null)
    const [streetViewError, setStreetViewError] = useState(false)
    const [streetViewRef, setStreetViewRef] = useState(null)
    const [streetViewService, setStreetViewService] = useState(null)
    const { imageAngle } = useImageCollectionStateContext()
    const { setDisplayedImagePosition, setImageAngle } = useImageCollectionDispatchContext()
    const { getMapRef, setMapBoundingBoxToFit } = useMapDispatchContext()
    const isMobile = useIsMobileDevice()

    const lastAngleRef = useRef(null)
    if (typeof imageAngle === "number") {
        lastAngleRef.current = imageAngle
    }

    const position = useMemo(() => {
        return {
            lat: initialPosition?.[1],
            lng: initialPosition?.[0],
        }
    }, [initialPosition])

    useEffect(() => {
        if (!streetViewService || !position.lat || !position.lng) {
            return undefined
        }
        let cancelled = false
        setStreetViewState(null)
        setStreetViewError(false)
        streetViewService.getPanorama({
            location: position,
            radius: 50,
        }, (data, status) => {
            if (cancelled) {
                return
            }
            if (status === "OK" && !!data) {
                setStreetViewState(data)
            } else {
                setStreetViewError(true)
            }
        })
        return () => {
            cancelled = true
        }
    }, [streetViewService, position])

    const mapStyle = useMemo(() => {
        const displayed = streetViewState && !streetViewError

        return ({
            ...Styled.mapContainerStyle,
            display: displayed ? "block" : "none",
        })
    }, [streetViewError, streetViewState])

    const onPositionChanged = useCallback(() => {
        if (active && streetViewRef) {
            const lat = streetViewRef.getPosition().lat()
            const lng = streetViewRef.getPosition().lng()
            setDisplayedImagePosition({ lat, lng })

            const map = getMapRef()?.getMap()
            if (map) {
                setMapBoundingBoxToFit({ minLng: lng, minLat: lat, maxLng: lng, maxLat: lat }, map.getZoom())
            }
        }
    }, [active, streetViewRef, setDisplayedImagePosition, getMapRef, setMapBoundingBoxToFit])

    const onPovChanged = useCallback(() => {
        if (active && streetViewRef) {
            const angle = normalizeAngle(streetViewRef.getPov().heading)
            lastAngleRef.current = angle
            setImageAngle(angle)
        }
    }, [active, streetViewRef, setImageAngle])

    const syncAngle = useCallback((panorama) => {
        const pov = panorama.getPov()
        if (typeof lastAngleRef.current === "number") {
            panorama.setPov({ ...pov, heading: normalizeAngle(lastAngleRef.current) })
        } else {
            lastAngleRef.current = normalizeAngle(pov.heading)
        }
        setImageAngle(lastAngleRef.current)
    }, [setImageAngle])

    const message = useMemo(() => {
        return streetViewError ? <StreetViewError />
            : !streetViewState ? <Styled.Skeleton />
                : null
    }, [streetViewError, streetViewState])

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env["NX_PUBLIC_GOOGLEMAPTOKEN"],
    })

    const hasNoStreetView = !!loadError || !position.lat || !position.lng || streetViewError

    useEffect(() => {
        if (active && hasNoStreetView) {
            setDisplayedImagePosition(null)
            setImageAngle(null)
        }
    }, [active, hasNoStreetView, setDisplayedImagePosition, setImageAngle])

    useEffect(() => {
        if (active && streetViewRef) {
            window.google?.maps?.event?.trigger(streetViewRef, "resize")
            onPositionChanged()
            syncAngle(streetViewRef)
        }
    }, [active, streetViewRef, onPositionChanged, syncAngle])

    const handleZoomIn = useCallback(() => {
        if (streetViewRef) {
            const currentZoom = streetViewRef.getZoom()
            streetViewRef.setZoom(currentZoom + 1)
        }
    }, [streetViewRef])

    const handleZoomOut = useCallback(() => {
        if (streetViewRef) {
            const currentZoom = streetViewRef.getZoom()
            streetViewRef.setZoom(currentZoom - 1)
        }
    }, [streetViewRef])

    if (loadError || !position.lat || !position.lng) {
        return <StreetViewError />
    }

    if (!isLoaded) {
        return <Styled.Skeleton />
    }

    return <Styled.MapWrapper>
        <GoogleMap
            center={position}
            id="google-map"
            mapContainerStyle={mapStyle}
            zoom={17}
        >
            {
                streetViewState?.tiles && <StreetViewPanorama
                    options={{
                        disableDefaultUI: true,
                        addressControl: true,
                        imageDateControl: true,
                        motionTracking: false,
                        motionTrackingControl: false,
                        zoomControl: false,
                    }}
                    position={position}
                    streetViewPanorama={streetViewState}
                    visible
                    onLoad={e => {
                        setStreetViewRef(e)
                        if (!streetViewState?.listener) {
                            setStreetViewState(previousState => ({
                                ...previousState,
                                listener: true,
                            }))
                        }
                    }}
                    onPositionChanged={onPositionChanged}
                    onPovChanged={onPovChanged}
                />
            }
            <StreetViewService
                onLoad={setStreetViewService}
            />
            {!isMobile && <Styled.BottomRightAnchor>
                <ZoomButtons
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                />
            </Styled.BottomRightAnchor>}
        </GoogleMap>
        {message}
    </Styled.MapWrapper>
}

function normalizeAngle(angle) {
    return Math.round((angle % 360 + 360) % 360)
}